import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { profileApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { BrandLoader } from '../components/BrandLoader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { User, Upload, FileText, Calendar, AlertTriangle, CheckCircle2, Trash2, Download, Loader2, Clock, Lock, Phone, Pencil, Building2, Mail, MapPin, Globe, Briefcase, Hash } from 'lucide-react';
import { PasswordInput } from '../components/ui/password-input';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';
const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
};

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadHasExpiry, setUploadHasExpiry] = useState(false);
  const [uploadExpiryDate, setUploadExpiryDate] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  // Change-password state
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);
  // iter112 — Inline phone edit
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  const handleSavePhone = async () => {
    const next = (phoneDraft || '').trim();
    setSavingPhone(true);
    try {
      await profileApi.updateMe({ phone: next });
      setProfile((p) => ({ ...(p || {}), phone: next }));
      setEditingPhone(false);
      toast.success('Phone updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update phone');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    if (pwNew.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setPwSubmitting(true);
    try {
      await profileApi.changePassword(pwCurrent, pwNew);
      toast.success('Password changed successfully');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setPwSubmitting(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await profileApi.get();
      setProfile(res.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const openUploadDialog = (docType, hasExpiry) => {
    const alreadyUploaded = (profile?.profile_documents || []).some((d) => d.doc_type === docType);
    if (alreadyUploaded) {
      if (!window.confirm(`This will replace your existing ${docType}. The previous file will be deleted permanently. Continue?`)) {
        return;
      }
    }
    setUploadDocType(docType);
    setUploadHasExpiry(hasExpiry);
    setUploadExpiryDate('');
    setUploadFile(null);
    setShowUploadDialog(true);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) { toast.error('Please select a file'); return; }
    if (uploadHasExpiry && !uploadExpiryDate) { toast.error('Expiry date is required for this document'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('doc_type', uploadDocType);
      if (uploadHasExpiry && uploadExpiryDate) {
        formData.append('expires_at', uploadExpiryDate);
      }
      await profileApi.uploadDocument(formData);
      toast.success(`${uploadDocType} saved`);
      setShowUploadDialog(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, docType) => {
    if (!confirm(`Delete ${docType}?`)) return;
    try {
      await profileApi.deleteDocument(docId);
      toast.success('Document deleted');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await profileApi.downloadDocument(docId);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Profile">
        <div className="flex items-center justify-center h-64"><BrandLoader size="md" label="Loading your profile…" /></div>
      </DashboardLayout>
    );
  }

  const profileDocs = profile?.profile_documents || [];
  const requiredDocs = profile?.required_document_types || [];

  return (
    <DashboardLayout title="My Profile">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
        {/* Basic Info — iter167: hidden for the Company OWNER (is_company_owner=True)
            because their personal Name / Email always equals the Company Name /
            Contact Email rendered in the card below, which users found redundant.
            Non-owner Company Reps still see this so they can manage their
            personal phone number etc. */}
        {!(profile?.role === 'COMPANY_REP' && profile?.is_company_owner) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><User className="w-5 h-5" /> Profile Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Name</Label>
                <p className="font-medium">{profile?.name}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <p className="font-medium">{profile?.email}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Role</Label>
                <Badge variant="outline">{profile?.role}</Badge>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Badge className={profile?.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {profile?.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {/* iter112 — Phone (display + edit) */}
              <div className="col-span-2">
                <Label className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone Number</Label>
                {editingPhone ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="tel"
                      placeholder="+966 5x xxx xxxx"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      className="max-w-xs"
                      autoFocus
                      data-testid="profile-phone-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleSavePhone}
                      disabled={savingPhone}
                      className="bg-blue-900 hover:bg-blue-950"
                      data-testid="profile-phone-save-btn"
                    >
                      {savingPhone ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setEditingPhone(false); setPhoneDraft(profile?.phone || ''); }}
                      data-testid="profile-phone-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-medium" data-testid="profile-phone-value">
                      {profile?.phone || <span className="text-slate-400">—</span>}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-blue-700"
                      onClick={() => { setPhoneDraft(profile?.phone || ''); setEditingPhone(true); }}
                      data-testid="profile-phone-edit-btn"
                      title="Edit phone number"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              {profile?.specialty_names?.length > 0 && (
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Specialties</Label>
                  <div className="flex gap-1.5 mt-1">
                    {profile.specialty_names.map((s, i) => <Badge key={i} variant="outline">{s}</Badge>)}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* iter167 — Company Information (only visible if user is linked to a Company Account) */}
        {profile?.company_account && (
          <Card data-testid="company-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Company Information
              </CardTitle>
              <CardDescription>
                Your organisation profile on ClinicalFlow. To request changes, contact the platform admin.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company Name</Label>
                  <p className="font-medium" data-testid="company-name">{profile.company_account.name || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Contact Email</Label>
                  <p className="font-medium" data-testid="company-contact-email">{profile.company_account.contact_email || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Company Phone</Label>
                  <p className="font-medium" data-testid="company-phone">{profile.company_account.phone || <span className="text-slate-400">—</span>}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</Label>
                  <p className="font-medium whitespace-pre-line" data-testid="company-address">
                    {profile.company_account.address || <span className="text-slate-400">—</span>}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Globe className="w-3 h-3" /> Website</Label>
                  {profile.company_account.website ? (
                    <a
                      href={profile.company_account.website.startsWith('http') ? profile.company_account.website : `https://${profile.company_account.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-700 hover:underline break-all"
                      data-testid="company-website"
                    >
                      {profile.company_account.website}
                    </a>
                  ) : (
                    <p className="font-medium text-slate-400">—</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Briefcase className="w-3 h-3" /> Industry</Label>
                  <p className="font-medium" data-testid="company-industry">{profile.company_account.industry || <span className="text-slate-400">—</span>}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Hash className="w-3 h-3" /> Tax ID</Label>
                  <p className="font-medium" data-testid="company-tax-id">{profile.company_account.tax_id || <span className="text-slate-400">—</span>}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Badge
                    className={
                      (profile.company_account.status || 'active') === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }
                    data-testid="company-status"
                  >
                    {(profile.company_account.status || 'active').charAt(0).toUpperCase() + (profile.company_account.status || 'active').slice(1)}
                  </Badge>
                </div>
                {typeof profile.company_account.reps_count === 'number' && (
                  <div>
                    <Label className="text-xs text-slate-500">Active Reps</Label>
                    <p className="font-medium" data-testid="company-reps-count">{profile.company_account.reps_count}</p>
                  </div>
                )}
                {profile.company_account.created_at && (
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Member Since</Label>
                    <p className="font-medium" data-testid="company-created-at">{formatDate(profile.company_account.created_at)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Change Password */}
        <Card data-testid="change-password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Change Password</CardTitle>
            <CardDescription>Update your account password. You&apos;ll stay signed in after the change.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="pw-current">Current password</Label>
                <PasswordInput
                  id="pw-current"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="profile-current-password"
                />
              </div>
              <div>
                <Label htmlFor="pw-new">New password</Label>
                <PasswordInput
                  id="pw-new"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="profile-new-password"
                />
                <p className="text-xs text-slate-500 mt-1">At least 6 characters.</p>
              </div>
              <div>
                <Label htmlFor="pw-confirm">Confirm new password</Label>
                <PasswordInput
                  id="pw-confirm"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="profile-confirm-password"
                />
              </div>
              <Button
                type="submit"
                disabled={pwSubmitting || !pwCurrent || !pwNew || !pwConfirm}
                data-testid="profile-change-password-btn"
              >
                {pwSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" />Update Password</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Profile Documents</CardTitle>
            <CardDescription>
              {requiredDocs.length > 0
                ? `Upload required documents for your role. ${requiredDocs.filter(d => d.enforce === 'block_assign').length} document(s) are required before you can be assigned to requests.`
                : 'Upload documents for your profile'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* iter121 — group required docs by request type (FT → CTA → FEA) with sub-headers */}
            {requiredDocs.length > 0 && (() => {
              const groupOrder = ['FT', 'CTA', 'FEA'];
              const groups = { FT: [], CTA: [], FEA: [], OTHER: [] };
              for (const d of requiredDocs) {
                const rt = d.for_request_type;
                if (groups[rt]) groups[rt].push(d);
                else groups.OTHER.push(d);
              }
              const renderDoc = (reqDoc, key) => {
                const uploaded = profileDocs.find(d => d.doc_type === reqDoc.name);
                const expiryDays = uploaded?.expires_at ? getDaysUntil(uploaded.expires_at) : null;
                const isExpired = expiryDays !== null && expiryDays <= 0;
                const isExpiring = expiryDays !== null && expiryDays > 0 && expiryDays <= 30;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => openUploadDialog(reqDoc.name, reqDoc.has_expiry)}
                    className={`group w-full text-left p-2.5 rounded-md border transition-all hover:shadow-sm hover:-translate-y-px ${
                      uploaded
                        ? (isExpired
                            ? 'border-red-200 bg-red-50 hover:bg-red-100'
                            : isExpiring
                              ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                              : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100')
                        : 'border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    data-testid={`doc-${reqDoc.name.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {uploaded ? (
                          isExpired ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" /> :
                          isExpiring ? <Clock className="w-4 h-4 text-amber-500 shrink-0" /> :
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        ) : (
                          <Upload className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-sm">{reqDoc.name}</span>
                            {reqDoc.has_expiry && <Badge variant="outline" className="text-[9px] h-4">Expires</Badge>}
                            {reqDoc.enforce === 'block_assign' && <Badge className="bg-red-100 text-red-600 text-[9px] h-4">Required</Badge>}
                          </div>
                          {uploaded ? (
                            <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                              {uploaded.filename} · Uploaded {formatDate(uploaded.uploaded_at)}
                              {uploaded.expires_at && (
                                <span className={isExpired ? ' text-red-600 font-medium' : isExpiring ? ' text-amber-600' : ''}>
                                  {' '}· Expires {formatDate(uploaded.expires_at)}
                                  {isExpired && ' (EXPIRED)'}
                                  {isExpiring && ` (${expiryDays}d left)`}
                                </span>
                              )}
                            </p>
                          ) : (
                            <p className="text-[11px] text-slate-400 mt-0.5 group-hover:text-blue-600 transition-colors">
                              Click to upload …
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {uploaded && (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleDownload(uploaded.id, uploaded.filename)}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => handleDelete(uploaded.id, reqDoc.name)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <span className="ml-1 text-[10px] uppercase tracking-wide font-medium text-slate-400 group-hover:text-blue-600 transition-colors">
                          {uploaded ? 'Replace ›' : 'Upload ›'}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              };
              return (
                <div className="space-y-4">
                  {groupOrder.map((rt) => {
                    if (!groups[rt] || groups[rt].length === 0) return null;
                    return (
                      <div key={rt} className="space-y-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] px-1.5 py-0">{rt}</Badge>
                          <span>{rt} Requirements</span>
                        </div>
                        <div className="space-y-2">
                          {groups[rt].map((d, i) => renderDoc(d, `${rt}-${i}`))}
                        </div>
                      </div>
                    );
                  })}
                  {groups.OTHER.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Other Requirements</div>
                      <div className="space-y-2">{groups.OTHER.map((d, i) => renderDoc(d, `other-${i}`))}</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Additional Documents section removed — users only upload role-required documents. */}
          </CardContent>
        </Card>

        {/* Upload Dialog with proper date picker */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload {uploadDocType}</DialogTitle>
              <DialogDescription>
                {uploadHasExpiry ? 'This document requires an expiry date.' : 'Select a file to upload.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>File *</Label>
                <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} className="mt-1" data-testid="upload-file-input" />
              </div>
              {uploadHasExpiry && (
                <div>
                  <Label>Expiry Date *</Label>
                  <Input type="date" value={uploadExpiryDate} onChange={(e) => setUploadExpiryDate(e.target.value)}
                    className="mt-1" data-testid="upload-expiry-input" />
                  <p className="text-xs text-slate-500 mt-1">Notifications will be sent before this date based on admin configuration.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button onClick={handleUploadSubmit} disabled={uploading || !uploadFile || (uploadHasExpiry && !uploadExpiryDate)}
                className="bg-blue-900 hover:bg-blue-950" data-testid="upload-submit-btn"
              >
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
